import type { ComponentType, ReactNode } from 'react';
import type { SocialIconName } from '../icon/SocialIcon';
import { SocialIcon } from '../icon/SocialIcon';
import { FooterCopyright } from './FooterCopyright';
import { FooterDivider } from './FooterDivider';
import { FooterLink, type FooterLinkProps } from './FooterLink';
import { FooterLinks } from './FooterLinks';
import { FooterPaymentIcons } from './FooterPaymentIcons';
import { FooterSection } from './FooterSection';
import { FooterSectionLink } from './FooterSectionLink';
import { FooterSections } from './FooterSections';
import { FooterSocial } from './FooterSocial';
import { FooterSocialLink } from './FooterSocialLink';
import { Footer } from './footer';

export type SiteFooterLink =
  /** `target` overrides the default new-tab behaviour for this row only. */
  { label: string; href: string; target?: '_blank' } | { label: ReactNode; asChild: true; key: string };

export interface SiteFooterSection {
  title: string;
  links: SiteFooterLink[];
}

export interface SiteFooterSocialLink {
  href: string;
  icon: SocialIconName;
  label: string;
}

export interface SiteFooterProps {
  paymentSectionTitle: string;
  sections: SiteFooterSection[];
  legalLinks: SiteFooterLink[];
  legalLinksAriaLabel: string;
  socialLinks: SiteFooterSocialLink[];
  socialLinksAriaLabel: string;
  copyright: string;
  className?: string;
}

function renderLink(LinkComponent: ComponentType<FooterLinkProps>, link: SiteFooterLink) {
  if ('asChild' in link) {
    return (
      <LinkComponent key={link.key} asChild>
        {link.label}
      </LinkComponent>
    );
  }
  return (
    <LinkComponent key={link.href} href={link.href} target={link.target}>
      {link.label}
    </LinkComponent>
  );
}

export function SiteFooter({
  paymentSectionTitle,
  sections,
  legalLinks,
  legalLinksAriaLabel,
  socialLinks,
  socialLinksAriaLabel,
  copyright,
  className,
}: SiteFooterProps) {
  return (
    <Footer className={className}>
      <FooterDivider />
      <FooterSections>
        <FooterSection title={paymentSectionTitle} defaultOpen>
          <FooterPaymentIcons />
        </FooterSection>
        {sections.map((section) => (
          <FooterSection key={section.title} title={section.title}>
            {section.links.map((link) => renderLink(FooterSectionLink, link))}
          </FooterSection>
        ))}
      </FooterSections>
      <FooterDivider className="hidden sm:block" />
      <FooterLinks aria-label={legalLinksAriaLabel}>{legalLinks.map((link) => renderLink(FooterLink, link))}</FooterLinks>
      <FooterDivider />
      <FooterSocial aria-label={socialLinksAriaLabel}>
        {socialLinks.map((link) => (
          <FooterSocialLink key={link.href} href={link.href} aria-label={link.label}>
            <SocialIcon name={link.icon} />
          </FooterSocialLink>
        ))}
      </FooterSocial>
      <FooterDivider />
      <FooterCopyright>{copyright}</FooterCopyright>
    </Footer>
  );
}
